import { Button, ButtonGroup, Col3Col9, DatePicker, DateUtil, Divider, Input, MessageHelper } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import { useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Constants from '../../../constants';
import ContentConstants from '../../../contentConstants';
import { AudiencePropertiesUnderValidation, AudienceStatus, IAudience } from '../../../models';
import { IApplicationState } from '../../../store';
import { ActionCreators } from '../../../store/SurveyAdminStore';
import { ValidationErrorHelper } from '../../../utilities';
import * as AudienceValidation from '../../audience/AudienceValidation';
import { ErrorMessageLabelNoIcon } from '../../audience/SharedComponents';
import { Timezone } from '../../audience/Timezone';
import { CardLayout, LabelAbove } from '../../common';
import { RightButtonContainer } from '../../common/styles/globalStyles';

const DateContainer = styled.div`
	min-height: 390px;
`;

const InputContainer = styled.div.attrs<{}, {}>({
	className: 'tws-date-container',
})`
	display: flex;
	margin-bottom: 15px;
	.tws-open-date-readonly {
		float: left;
		width: 218px;
		margin-right: 10px;
	}

	@media (min-width: 0) and (max-width: 1500px) {
		flex-direction: column;
	}
`;

export interface IAudienceRouteProps {
	audienceId?: number;
	isUpdatingAtSurveyLevel: boolean;
}

export interface IAdminReopenSurveyProps {
	audience: IAudience;
	audiences: IAudience[];
	isUpdatingAtSurveyLevel: boolean;
}

export function AdminReopenSurvey(props: IAudienceRouteProps) {
	const { audiences } = useSelector((state: IApplicationState) => state.surveyAdmin);
	let currentAudience;
	if (props.audienceId !== undefined) {
		currentAudience = audiences.filter((a) => a.audienceId === props.audienceId)[0];
	} else {
		currentAudience = audiences[0];
	}

	if (!currentAudience) {
		return <CardLayout loading={true} title={ContentConstants.SURVEY_ADMIN_ACTION_REOPEN_SURVEY} />;
	} else {
		return <ReopenSurvey audience={currentAudience} audiences={audiences} isUpdatingAtSurveyLevel={props.isUpdatingAtSurveyLevel} />;
	}
}

function ReopenSurvey(props: IAdminReopenSurveyProps) {
	const { surveyAdminSummary } = useSelector((state: IApplicationState) => state.surveyAdmin);
	const [audience, updateAudience] = useState(props.audience);

	const dispatch = useDispatch();

	return (
		<CardLayout loading={audience === undefined} title={ContentConstants.SURVEY_ADMIN_ACTION_REOPEN_SURVEY}>
			<DateContainer>{getDateContent()}</DateContainer>
			{getSaveContent()}
		</CardLayout>
	);

	function getAudienceOpenDate() {
		return (audience.openImmediately ? surveyAdminSummary && surveyAdminSummary.publishedDate : audience.openOn) ?? Date.now().toString();
	}

	function getDateContent() {
		const validationErrorHelper = new ValidationErrorHelper<AudiencePropertiesUnderValidation>(getValidationError());
		const openOnDate = getAudienceOpenDate();

		return (
			<>
				<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_INSTRUCTIONS}>
					<LabelAbove type='input' textIsHtml={true} text={ContentConstants.SURVEY_ADMIN_SURVEY_REOPEN_INSTRUCTIONS} />
					<InputContainer>
						<Input
							className={'tws-open-date-readonly'}
							readonly={true}
							value={DateUtil.formatDateToLocal(new Date(openOnDate), Constants.DATE_FORMAT_LONG)}
						/>
						<DatePicker
							className={'tws-close-date-picker'}
							defaultGap={0}
							value={audience.closeOn}
							textInputPlaceholder={ContentConstants.DATEPICKER_CLOSE_DATE}
							clearText={ContentConstants.DATEPICKER_CLEARTEXT}
							enabledStartDate={moment()}
							onChange={onCloseChange}
							onClear={onCloseClear}
							error={!validationErrorHelper.IsValid(AudiencePropertiesUnderValidation.CloseOn)}
							message={validationErrorHelper.Errors(AudiencePropertiesUnderValidation.CloseOn)}
							dateFormat={Constants.DATE_FORMAT_LONG}
						/>
					</InputContainer>

					{!validationErrorHelper.IsValid(AudiencePropertiesUnderValidation.OpenCloseDates) && (
						<ErrorMessageLabelNoIcon className='tws-open-close-dates-error-message'>
							{MessageHelper.getFormattedErrorMessages(validationErrorHelper.Errors(AudiencePropertiesUnderValidation.OpenCloseDates))}
						</ErrorMessageLabelNoIcon>
					)}
				</Col3Col9>
				<Col3Col9 label={ContentConstants.AUDIENCE_TIMEZONE_SECTION_LABEL}>
					<Timezone />
				</Col3Col9>
			</>
		);
	}

	function getSaveContent() {
		const validationErrorHelper = new ValidationErrorHelper<AudiencePropertiesUnderValidation>(getValidationError());
		return (
			<>
				<Divider type='dashed' />
				<RightButtonContainer>
					<ButtonGroup type='column' reversed={false}>
						<Button id='SaveButton' color='primary' size='lg' disabled={!validationErrorHelper.IsValid} onClick={() => onSave()}>
							{ContentConstants.BUTTON_SAVE_CHANGES}
						</Button>
					</ButtonGroup>
				</RightButtonContainer>
			</>
		);
	}

	function getValidationError() {
		const openOnDate = getAudienceOpenDate();
		return AudienceValidation.validateAudienceCloseDates(openOnDate, audience.closeOn);
	}

	function onCloseChange(date: any) {
		updateAudience({ ...audience, closeOn: getUtcDate(date) });
	}

	function onCloseClear() {
		updateAudience({ ...audience, closeOn: undefined });
	}

	function getUtcDate(dateString: string) {
		return DateUtil.getUtcDateTime(new Date(dateString)).toString();
	}

	function onSave() {
		if (props.isUpdatingAtSurveyLevel) {
			const closedDate = audience.closeOn;
			const closedAudiences = props.audiences.filter((a) => a.audienceStatus === AudienceStatus.Closed);
			const reopendAudiences = closedAudiences.map((a) => ({
				...a,
				closeOn: closedDate,
				isOpen: true,
				isClosed: false,
				audienceStatus: AudienceStatus.Live,
			}));
			dispatch(ActionCreators.UpdateAudiences(reopendAudiences, true));
		} else {
			const reopenedAudience = { ...audience, isOpen: true, isClosed: false, audienceStatus: AudienceStatus.Live };
			dispatch(ActionCreators.UpdateAudience(reopenedAudience, true));
		}
	}
}
